/** The run-level sums of a debrief: requisition earned and what the enemy learned (04 §2.3, §6). */

import { clamp } from '../../sim/math';
import type { HazardRules } from '../content/career';
import { READINESS, REWARD } from '../content/economy';
import type { MissionSummary, Operation, TheatreNode } from '../types';

/** RP for a mission: the node's reward x its multiplier x a factor from the score. */
export function rpEarned(node: TheatreNode, summary: MissionSummary, success: boolean): number {
  const score = Math.max(0, summary.score);
  const factor = success
    ? clamp(REWARD.successFloor + score / REWARD.scorePerUnit, REWARD.successFloor, REWARD.successCap)
    : Math.min(REWARD.failCap, score / REWARD.failScorePerUnit);
  return Math.round(node.rewardRp * node.rewardMultiplier * factor);
}

/** The RP range a node card shows: worst and best successful outcome. */
export function rewardRange(node: TheatreNode): [min: number, max: number] {
  const full = node.rewardRp * node.rewardMultiplier;
  return [Math.round(full * REWARD.successFloor), Math.round(full * REWARD.successCap)];
}

export interface ReadinessChange {
  delta: number;
  reasons: string[];
}

const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);

/** The readiness table (04 §2.3), with a reason for every line so the debrief can show its working. */
export function readinessFor(
  summary: MissionSummary, success: boolean, keystone: boolean, rules: HazardRules,
): ReadinessChange {
  const lines: Array<[number, string]> = [];
  if (summary.outcome === 'scrubbed') {
    lines.push([READINESS.scrubbed, 'Mission scrubbed: the aircraft was seen and nothing was done']);
  } else {
    lines.push([READINESS.attempted, 'A mission was attempted']);
    if (summary.outcome === 'extracted' && summary.maxAlert <= READINESS.quietAlert && rules.quietCools) {
      lines.push([READINESS.quietExtraction, 'Extracted while the garrison was quiet']);
    }
    if (summary.outcome === 'extracted' && summary.maxAlert >= READINESS.alarmAlert) {
      lines.push([READINESS.alarmExtraction, 'Extracted under a full alarm']);
    }
    if (!success) lines.push([READINESS.failed, 'The mission failed']);
    if (summary.outcome === 'captured') lines.push([READINESS.captured, 'A ranger was captured - they talked']);
    if (success && keystone) lines.push([READINESS.keystone, 'Keystone completed']);
  }
  return { delta: lines.reduce((sum, [n]) => sum + n, 0), reasons: lines.map(([n, why]) => `${why} (${signed(n)})`) };
}

/** Apply a change within 0-10; returns what actually moved. */
export function changeReadiness(op: Operation, delta: number): number {
  const before = op.readiness;
  op.readiness = clamp(before + delta, READINESS.min, READINESS.max);
  return op.readiness - before;
}
